import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import PanoViewer from './PointViewer';
import PanoEvents from '../Enumerations/ViewerEnums';
import PanoConstants from '../constants/ViewerConstants';
interface IBoxProperties {
    id: string;
    realWorldPos: BABYLON.Vector3;
    xDim: number;
    yDim: number;
    zDim: number;
    displayObstructed: boolean;
}

interface ISceneBoxProperties extends IBoxProperties {
    scenePos: BABYLON.Vector3;
}

class BoxManager {
    public static BOX_NAME = "annBox";
    private static DefaultBoxColor = "#00ff007f";

    private _viewer: PanoViewer;

    //private _boxMeshes: BABYLON.Mesh[] = [];
    private _boxMeshes: { [labelID: string]: BABYLON.Mesh; } = {};
    private _boxProperties: { [labelID: string]: IBoxProperties; } = {};
    
    private _boxColor: string = BoxManager.DefaultBoxColor;
    private _boxMaterial: BABYLON.StandardMaterial;

    private _attachedGizmo: BABYLON.IGizmo | null = null;
    private _attachedGizmoId: string;

    public events: EventTarget = new EventTarget();

    private get viewer() { return this._viewer; }
    private get scene() { return this._viewer.scene; }
    private get engine() { return this.scene.getEngine(); }

    constructor(viewer: PanoViewer) {
        this._viewer = viewer;

        this.scene.onPointerObservable.add(this._handlePointerObservable);
        this.scene.onKeyboardObservable.add((evt) => { this._handleKeyboardObservable(evt); });
        this.scene.onBeforeCameraRenderObservable.add(this._handleBeforeCameraRenderObservable);
    }

    public dispose() {
        this.clearBoxes();

        this.scene.onPointerObservable.removeCallback(this._handlePointerObservable);
        this.scene.onBeforeCameraRenderObservable.removeCallback(this._handleBeforeCameraRenderObservable);
    }

    public clearBoxes() {
        this._attachedGizmo?.dispose();
        this._attachedGizmo = null;

        this._attachedGizmoId = "";
         
        var keys = Object.keys(this._boxMeshes);

        for (var x = 0; x < keys.length; x++) {
            if (this._boxMeshes[keys[x]] != null) {
                this._boxMeshes[keys[x]].dispose();
                this._boxMeshes[keys[x]] = null;
            }
        }

        this._boxMeshes = {};

        this._advancedTexture?.dispose();
        this._advancedTexture = null;
    }

    public createBox(boxProps: IBoxProperties) {
        this._createDemoBoxAtWorldPosition(boxProps);
    }

    public setBoxesColor(color: string) {
        this._boxColor = color;
        this._boxMaterial = null;

        var keys = Object.keys(this._boxMeshes);

        for (var x = 0; x < keys.length; x++) {
            if (this._boxMeshes[keys[x]] != null) {
                this._boxMeshes[keys[x]].material = this._getBoxMaterial();
            }
        }
    }

    public getBoxProperties(labelID:string) {
        return this._boxProperties[labelID];
    }

    public setPositioningMode(mode: "move" | "scale" | "none") {
        if (mode == "move") {
            this.createPositionGizmo();
        }
        else if (mode == "scale") {
            this.createScaleGizmo();
        }
        else {
            this._attachedGizmo?.dispose();
            this._attachedGizmo = null;
        }
    }

    public setSelectedBox(selectedBoxID: string) {
        this._attachedGizmoId = selectedBoxID;
    }

    private _handleKeyboardObservable = (evt: BABYLON.KeyboardInfo) => {
        switch (evt.type) {
            case BABYLON.KeyboardEventTypes.KEYDOWN:
                if (evt.event.key == "g") {
                    this.createPositionGizmo();
                }
                else if (evt.event.key == "h") {
                    this.createScaleGizmo();
                }
                break;
        }
    }

    private _handleBeforeCameraRenderObservable = (camera: BABYLON.Camera) => {
    }

    private _handlePointerObservable = (evt: BABYLON.PointerInfo) => {
        if (evt.event.button === 2) {
            if (this._attachedGizmo) {
                var released = false;
                if ((this._attachedGizmo as any).xGizmo && (this._attachedGizmo as any).xGizmo.dragBehavior.dragging) {
                    (this._attachedGizmo as any).releaseDrag();
                    released = true;
                }
                if ((this._attachedGizmo as any).yGizmo && (this._attachedGizmo as any).yGizmo.dragBehavior.dragging) {
                    (this._attachedGizmo as any).releaseDrag();
                    released = true;
                }
                if ((this._attachedGizmo as any).zGizmo && (this._attachedGizmo as any).zGizmo.dragBehavior.dragging) {
                    (this._attachedGizmo as any).releaseDrag();
                    released = true;
                }
                if ((this._attachedGizmo as any).uniformScaleGizmo && (this._attachedGizmo as any).uniformScaleGizmo.dragBehavior.dragging) {
                    (this._attachedGizmo as any).releaseDrag();
                    released = true;
                }
                if (released) {

                    
                    if ((this._attachedGizmo as any).uniformScaleGizmo) {
                        this._attachedGizmo?.dispose();
                        this._attachedGizmo = null;
                        this.createScaleGizmo();
                    }
                    else {
                        this._attachedGizmo?.dispose();
                        this._attachedGizmo = null;
                        this.createPositionGizmo();
                    }
                }
            }
        }
    };

    private _createDemoBoxAtWorldPosition(boxProps: IBoxProperties) {
        
        const scenePos = boxProps.realWorldPos;

        this._boxMeshes[boxProps.id] = this._createDemoBox({ scenePos: scenePos, ...boxProps }, this.scene);
        this._boxMeshes[boxProps.id].isPickable = false;
        this._boxProperties[boxProps.id] = boxProps;
    }

    private _moveDemoBox(distance: number, axis: BABYLON.Vector3) {
        if (distance == 0 || this._boxProperties[this._attachedGizmoId] == null)
            return;

        const moveVec = axis.scale(distance);
        this._boxProperties[this._attachedGizmoId].realWorldPos.addInPlace(moveVec);

        this._createDemoBoxAtWorldPosition(this._boxProperties[this._attachedGizmoId]);
    }

    private _scaleDemoBox(scale: number, axis: BABYLON.Vector3) {
        if (scale <= 0 || this._boxProperties[this._attachedGizmoId] == null)
            return;

        if (axis.x != 0)
            this._boxProperties[this._attachedGizmoId].xDim *= scale;
        if (axis.y != 0)
            this._boxProperties[this._attachedGizmoId].yDim *= scale;
        if (axis.z != 0)
            this._boxProperties[this._attachedGizmoId].zDim *= scale;

        this._createDemoBoxAtWorldPosition(this._boxProperties[this._attachedGizmoId]);
    }

    private _createDemoBox(boxProps: ISceneBoxProperties, scene: BABYLON.Scene) {
        
        if (this._boxMeshes[boxProps.id] != null) {
            this._boxMeshes[boxProps.id].dispose();
            this._boxMeshes[boxProps.id] = null;
        }

        var box = BABYLON.MeshBuilder.CreateBox(BoxManager.BOX_NAME, { width: boxProps.zDim, height: boxProps.yDim, depth: boxProps.xDim }, scene);
        box.position = boxProps.realWorldPos;
        box.material = this._getBoxMaterial();

        if (boxProps.displayObstructed)
            box.renderingGroupId = 1;

        if (this._attachedGizmo && this._attachedGizmoId == boxProps.id) {
            this._attachedGizmo.attachedMesh = box;
        }

        return box;
    }

    private _getBoxMaterial() {
        if (!this._boxMaterial) {
            const mat = new BABYLON.StandardMaterial("boxMat", this.scene);
            const boxColorVec = BABYLON.Color4.FromHexString(this._boxColor);

            mat.alpha = boxColorVec.a;
            mat.diffuseColor = new BABYLON.Color3(boxColorVec.r, boxColorVec.g, boxColorVec.b);
            mat.specularColor = new BABYLON.Color3(1, 1, 1);
            mat.emissiveColor = new BABYLON.Color3(0, 0, 0);
            mat.ambientColor = new BABYLON.Color3(0, 1, 0);
            this._boxMaterial = mat;
        }
        return this._boxMaterial;
    }
     
    private createPositionGizmo() {
        var gizmo = new BABYLON.PositionGizmo();
        this.setupCustomPositionDragEndHandlder(gizmo);

        const dragConstant = 1.0;

        gizmo.xGizmo.dragBehavior.onDragObservable.clear();
        gizmo.yGizmo.dragBehavior.onDragObservable.clear();
        gizmo.zGizmo.dragBehavior.onDragObservable.clear();

        gizmo.xGizmo.dragBehavior.onDragObservable.add((event) => {
            this._moveDemoBox(event.dragDistance * dragConstant, BABYLON.Vector3.Right());
        });
        gizmo.yGizmo.dragBehavior.onDragObservable.add((event) => {
            this._moveDemoBox(event.dragDistance * dragConstant, BABYLON.Vector3.Up());
        });
        gizmo.zGizmo.dragBehavior.onDragObservable.add((event) => {
            this._moveDemoBox(event.dragDistance * dragConstant, BABYLON.Vector3.Forward());
        });

        gizmo.onDragEndObservable.add(() => {
            this.events?.dispatchEvent(new Event(PanoEvents.BoxManager.BOX_CHANGED));
        });

        this.attachGizmo(gizmo);
    }

    /* This is a custom override of the release drag code for the PointerDragBehavior.
    Specifically, the release drag would reattach the camera (since it gets temporarily unattached
    so that mouse moves don't rotate the camera). The stock babylon code would reattach the camera,
    but it would screw up our implementation of events (mouse wheel, and rotate) for the photo viewer.
    Therefore, we override this behavior, and change the reattach camera code only. BABYLON upgrades should
    confirm that no changes have been made to the release drag code, and update this code appropriately.
    */
    private setupCustomScaleDragEndHandlder(gizmo: BABYLON.ScaleGizmo) {
        
        gizmo.xGizmo.dragBehavior.releaseDrag = () => 
        {
            var that = gizmo.xGizmo.dragBehavior as any;
            if (that.dragging) {
                that.dragging = false;
                that.onDragEndObservable.notifyObservers({ dragPlanePoint: that.lastDragPosition, pointerId: that.currentDraggingPointerId, pointerInfo: that._activePointerInfo });
            }

            that.currentDraggingPointerId = -1;
            that._activeDragButton = -1;
            that._activePointerInfo = null;
            that._moving = false;

            // Reattach camera controls
            if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                if (that._scene.activeCamera.getClassName() === "ArcRotateCamera") {
                    const arcRotateCamera = that._scene.activeCamera as BABYLON.ArcRotateCamera;

                    arcRotateCamera.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                    // Adjust the double click delay to 500ms (default is 300ms)
                    BABYLON.Scene.DoubleClickDelay = 500;

                    (arcRotateCamera as any).inputs.attached.pointers.buttons = [0, 1] as any;

                    // Disable panning
                    arcRotateCamera.panningSensibility = 0;

                    // Detach default mousewheel handler, so that we can use our own mousewheel handling below
                    arcRotateCamera.inputs.attached.mousewheel.detachControl();

                    // Disable pinching
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanning = false;
                    arcRotateCamera.pinchToPanMaxDistance = 1e10;

                } else {
                    that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                }
                that._attachedToElement = false;
            }
        };

        gizmo.yGizmo.dragBehavior.releaseDrag = () => {
            var that = gizmo.yGizmo.dragBehavior as any;
            if (that.dragging) {
                that.dragging = false;
                that.onDragEndObservable.notifyObservers({ dragPlanePoint: that.lastDragPosition, pointerId: that.currentDraggingPointerId, pointerInfo: that._activePointerInfo });
            }

            that.currentDraggingPointerId = -1;
            that._activeDragButton = -1;
            that._activePointerInfo = null;
            that._moving = false;

            // Reattach camera controls
            if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                if (that._scene.activeCamera.getClassName() === "ArcRotateCamera") {
                    const arcRotateCamera = that._scene.activeCamera as BABYLON.ArcRotateCamera;

                    arcRotateCamera.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                    // Adjust the double click delay to 500ms (default is 300ms)
                    BABYLON.Scene.DoubleClickDelay = 500;

                    (arcRotateCamera as any).inputs.attached.pointers.buttons = [0, 1] as any;

                    // Disable panning
                    arcRotateCamera.panningSensibility = 0;

                    // Detach default mousewheel handler, so that we can use our own mousewheel handling below
                    arcRotateCamera.inputs.attached.mousewheel.detachControl();

                    // Disable pinching
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanning = false;
                    arcRotateCamera.pinchToPanMaxDistance = 1e10;

                } else {
                    that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                }
                that._attachedToElement = false;
            }
        };

        gizmo.zGizmo.dragBehavior.releaseDrag = () => {
            var that = gizmo.zGizmo.dragBehavior as any;
            if (that.dragging) {
                that.dragging = false;
                that.onDragEndObservable.notifyObservers({ dragPlanePoint: that.lastDragPosition, pointerId: that.currentDraggingPointerId, pointerInfo: that._activePointerInfo });
            }

            that.currentDraggingPointerId = -1;
            that._activeDragButton = -1;
            that._activePointerInfo = null;
            that._moving = false;

            // Reattach camera controls
            if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                if (that._scene.activeCamera.getClassName() === "ArcRotateCamera") {
                    const arcRotateCamera = that._scene.activeCamera as BABYLON.ArcRotateCamera;

                    arcRotateCamera.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                    // Adjust the double click delay to 500ms (default is 300ms)
                    BABYLON.Scene.DoubleClickDelay = 500;

                    (arcRotateCamera as any).inputs.attached.pointers.buttons = [0, 1] as any;

                    // Disable panning
                    arcRotateCamera.panningSensibility = 0;

                    // Detach default mousewheel handler, so that we can use our own mousewheel handling below
                    arcRotateCamera.inputs.attached.mousewheel.detachControl();

                    // Disable pinching
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanning = false;
                    arcRotateCamera.pinchToPanMaxDistance = 1e10;

                } else {
                    that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                }
                that._attachedToElement = false;
            }
        };

        gizmo.uniformScaleGizmo.dragBehavior.releaseDrag = () => {
            var that = gizmo.uniformScaleGizmo.dragBehavior as any;
            if (that.dragging) {
                that.dragging = false;
                that.onDragEndObservable.notifyObservers({ dragPlanePoint: that.lastDragPosition, pointerId: that.currentDraggingPointerId, pointerInfo: that._activePointerInfo });
            }

            that.currentDraggingPointerId = -1;
            that._activeDragButton = -1;
            that._activePointerInfo = null;
            that._moving = false;

            // Reattach camera controls
            if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                if (that._scene.activeCamera.getClassName() === "ArcRotateCamera") {
                    const arcRotateCamera = that._scene.activeCamera as BABYLON.ArcRotateCamera;

                    arcRotateCamera.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                    // Adjust the double click delay to 500ms (default is 300ms)
                    BABYLON.Scene.DoubleClickDelay = 500;

                    (arcRotateCamera as any).inputs.attached.pointers.buttons = [0, 1] as any;

                    // Disable panning
                    arcRotateCamera.panningSensibility = 0;

                    // Detach default mousewheel handler, so that we can use our own mousewheel handling below
                    arcRotateCamera.inputs.attached.mousewheel.detachControl();

                    // Disable pinching
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanning = false;
                    arcRotateCamera.pinchToPanMaxDistance = 1e10;

                } else {
                    that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                }
                that._attachedToElement = false;
            }
        };
    }

    /* This is a custom override of the release drag code for the PointerDragBehavior.
    Specifically, the release drag would reattach the camera (since it gets temporarily unattached
    so that mouse moves don't rotate the camera). The stock babylon code would reattach the camera,
    but it would screw up our implementation of events (mouse wheel, and rotate) for the photo viewer.
    Therefore, we override this behavior, and change the reattach camera code only. BABYLON upgrades should
    confirm that no changes have been made to the release drag code, and update this code appropriately.
    */
    private setupCustomPositionDragEndHandlder(gizmo: BABYLON.PositionGizmo) {
        gizmo.xGizmo.dragBehavior.releaseDrag = () => {
            var that = gizmo.xGizmo.dragBehavior as any;
            if (that.dragging) {
                that.dragging = false;
                that.onDragEndObservable.notifyObservers({ dragPlanePoint: that.lastDragPosition, pointerId: that.currentDraggingPointerId, pointerInfo: that._activePointerInfo });
            }

            that.currentDraggingPointerId = -1;
            that._activeDragButton = -1;
            that._activePointerInfo = null;
            that._moving = false;

            // Reattach camera controls
            if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                if (that._scene.activeCamera.getClassName() === "ArcRotateCamera") {
                    const arcRotateCamera = that._scene.activeCamera as BABYLON.ArcRotateCamera;

                    arcRotateCamera.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                    // Adjust the double click delay to 500ms (default is 300ms)
                    BABYLON.Scene.DoubleClickDelay = 500;

                    (arcRotateCamera as any).inputs.attached.pointers.buttons = [0, 1] as any;

                    // Disable panning
                    arcRotateCamera.panningSensibility = 0;

                    // Detach default mousewheel handler, so that we can use our own mousewheel handling below
                    arcRotateCamera.inputs.attached.mousewheel.detachControl();

                    // Disable pinching
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanning = false;
                    arcRotateCamera.pinchToPanMaxDistance = 1e10;

                } else {
                    that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                }
                that._attachedToElement = false;
            }
        };

        gizmo.yGizmo.dragBehavior.releaseDrag = () => {
            var that = gizmo.yGizmo.dragBehavior as any;
            if (that.dragging) {
                that.dragging = false;
                that.onDragEndObservable.notifyObservers({ dragPlanePoint: that.lastDragPosition, pointerId: that.currentDraggingPointerId, pointerInfo: that._activePointerInfo });
            }

            that.currentDraggingPointerId = -1;
            that._activeDragButton = -1;
            that._activePointerInfo = null;
            that._moving = false;

            // Reattach camera controls
            if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                if (that._scene.activeCamera.getClassName() === "ArcRotateCamera") {
                    const arcRotateCamera = that._scene.activeCamera as BABYLON.ArcRotateCamera;

                    arcRotateCamera.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                    // Adjust the double click delay to 500ms (default is 300ms)
                    BABYLON.Scene.DoubleClickDelay = 500;

                    (arcRotateCamera as any).inputs.attached.pointers.buttons = [0, 1] as any;

                    // Disable panning
                    arcRotateCamera.panningSensibility = 0;

                    // Detach default mousewheel handler, so that we can use our own mousewheel handling below
                    arcRotateCamera.inputs.attached.mousewheel.detachControl();

                    // Disable pinching
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanning = false;
                    arcRotateCamera.pinchToPanMaxDistance = 1e10;

                } else {
                    that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                }
                that._attachedToElement = false;
            }
        };

        gizmo.zGizmo.dragBehavior.releaseDrag = () => {
            var that = gizmo.zGizmo.dragBehavior as any;
            if (that.dragging) {
                that.dragging = false;
                that.onDragEndObservable.notifyObservers({ dragPlanePoint: that.lastDragPosition, pointerId: that.currentDraggingPointerId, pointerInfo: that._activePointerInfo });
            }

            that.currentDraggingPointerId = -1;
            that._activeDragButton = -1;
            that._activePointerInfo = null;
            that._moving = false;

            // Reattach camera controls
            if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                if (that._scene.activeCamera.getClassName() === "ArcRotateCamera") {
                    const arcRotateCamera = that._scene.activeCamera as BABYLON.ArcRotateCamera;

                    arcRotateCamera.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                    // Adjust the double click delay to 500ms (default is 300ms)
                    BABYLON.Scene.DoubleClickDelay = 500;

                    (arcRotateCamera as any).inputs.attached.pointers.buttons = [0, 1] as any;

                    // Disable panning
                    arcRotateCamera.panningSensibility = 0;

                    // Detach default mousewheel handler, so that we can use our own mousewheel handling below
                    arcRotateCamera.inputs.attached.mousewheel.detachControl();

                    // Disable pinching
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                    (arcRotateCamera.inputs.attached.pointers as any).multiTouchPanning = false;
                    arcRotateCamera.pinchToPanMaxDistance = 1e10;

                } else {
                    that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                }
                that._attachedToElement = false;
            }
        };
    }

    private createScaleGizmo() {
        var gizmo = new BABYLON.ScaleGizmo();

        this.setupCustomScaleDragEndHandlder(gizmo);
        this.attachGizmo(gizmo);

        const scaleConstant = 1.0;

        gizmo.xGizmo.dragBehavior.onDragObservable.clear();
        gizmo.yGizmo.dragBehavior.onDragObservable.clear();
        gizmo.zGizmo.dragBehavior.onDragObservable.clear();

        gizmo.xGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, BABYLON.Vector3.Forward());
        });
        gizmo.yGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, BABYLON.Vector3.Up());
        });
        gizmo.zGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, BABYLON.Vector3.Left());
        });
        gizmo.uniformScaleGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, new BABYLON.Vector3(1.0, 1.0, 1.0));
        });

        gizmo.onDragEndObservable.add(() => {

            this.events?.dispatchEvent(new Event(PanoEvents.BoxManager.BOX_CHANGED));
        });
    }

    private attachGizmo(gizmo: BABYLON.IGizmo) {
        this._attachedGizmo?.dispose();
        this._attachedGizmo = null;

        if (this._boxMeshes[this._attachedGizmoId] != null)
            gizmo.attachedMesh = this._boxMeshes[this._attachedGizmoId]

        //gizmo.updateGizmoRotationToMatchAttachedMesh = false;
        //gizmo.customRotationQuaternion = camera.rotationQuaternion;
        //gizmo.coordinatesMode = BABYLON.GizmoCoordinatesMode.Local;

        this._attachedGizmo = gizmo;
    }

    private _advancedTexture: BABYLONGUI.AdvancedDynamicTexture | null = null;
    private _debugTextBlock: BABYLONGUI.TextBlock | null = null;

    private _updateDemoBoxDebugText(): void {
        const boxProps = this._boxProperties[this._attachedGizmoId];
        const debugText = `X: ${boxProps.realWorldPos.x.toFixed(3)} m\nY: ${boxProps.realWorldPos.y.toFixed(3)} m\nZ: ${boxProps.realWorldPos.z.toFixed(3)} m\nX-dim: ${boxProps.xDim.toFixed(3)} m\nY-dim: ${boxProps.yDim.toFixed(3)} m\nZ-dim: ${boxProps.zDim.toFixed(3)} m`;

        this._createDebugTextBox(this.scene, debugText);
    }

    private _createDebugTextBox(scene: BABYLON.Scene, initialText: string): void {
        // Initialize only once
        if (!this._advancedTexture) {
            this._advancedTexture = BABYLONGUI.AdvancedDynamicTexture.CreateFullscreenUI("UI", true, scene);

            const rectangle = new BABYLONGUI.Rectangle();
            rectangle.width = "180px";
            rectangle.height = "150px";
            rectangle.cornerRadius = 20;
            rectangle.color = "White";
            rectangle.thickness = 2;
            rectangle.background = "Black";
            rectangle.alpha = 0.5; // Semi-transparent
            rectangle.verticalAlignment = BABYLONGUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
            rectangle.horizontalAlignment = BABYLONGUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
            rectangle.leftInPixels = -150;
            this._advancedTexture.addControl(rectangle);

            this._debugTextBlock = new BABYLONGUI.TextBlock();
            this._debugTextBlock.color = "white";
            this._debugTextBlock.textWrapping = true;
            this._debugTextBlock.textHorizontalAlignment = BABYLONGUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
            this._debugTextBlock.paddingLeft = "10px";
            rectangle.addControl(this._debugTextBlock);
        }

        // Update the text
        if (this._debugTextBlock) {
            this._debugTextBlock.text = initialText;
        }
    }

}

export default BoxManager;